import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useOnboardingHub } from '@/hooks/useOnboardingHub';
import { PartAStepper } from './PartAStepper';
import {
  AU_STATES,
  STATE_TIMEZONE,
  FIELD_LABELS,
  partACompanyIntro,
  partAMissing,
  screenMissing,
} from './partAFields';
import { deferPartA, leaveToLogin } from './partASignOut';

/**
 * Part A — "Set up your company". Mandatory blocker for company_admin.
 * Two screens, Save and continue / Done. Sign out and Finish later stay available.
 */
export function OnboardingHubModal({ open, onCompleted, onSignOut, onFinishLater }) {
  const { partA, loading, loadPartA, savePartA, completePartA } = useOnboardingHub();
  const [screen, setScreen] = useState('company');
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (!open) return;
    loadPartA()
      .then((data) => setForm({ ...(data?.values || {}) }))
      .catch(() => toast.error('Could not load company setup'));
  }, [open, loadPartA]);

  const locked = partA?.locked || {};
  const timezones = partA?.timezones || Object.values(STATE_TIMEZONE);

  const missingAll = useMemo(() => (form ? partAMissing(form, { abnLocked: !!locked.abn }) : []), [form, locked.abn]);
  const missingHere = useMemo(() => (form ? screenMissing(form, screen, { abnLocked: !!locked.abn }) : []), [form, screen, locked.abn]);

  const set = useCallback((key, value) => {
    setForm((f) => {
      const next = { ...f, [key]: value };
      if (key === 'state' && (!f.timezone || f.timezone === STATE_TIMEZONE[f.state])) {
        next.timezone = STATE_TIMEZONE[value] || f.timezone;
      }
      return next;
    });
    setTouched((t) => ({ ...t, [key]: true }));
  }, []);

  const showError = (key) => touched[key] && missingHere.includes(key);

  const handleContinue = async () => {
    if (!form) return;
    setTouched((t) => ({ ...t, ...Object.fromEntries(missingHere.map((k) => [k, true])) }));
    if (missingHere.length) {
      toast.error(`Complete ${missingHere.map((k) => FIELD_LABELS[k]).join(', ')}`);
      return;
    }
    setBusy(true);
    try {
      await savePartA(form);
      setScreen('you');
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  const handleDone = async () => {
    if (!form) return;
    setTouched((t) => ({ ...t, ...Object.fromEntries(missingAll.map((k) => [k, true])) }));
    if (missingAll.length) {
      const here = missingHere.length ? missingHere : missingAll;
      toast.error(`Complete ${here.map((k) => FIELD_LABELS[k]).join(', ')}`);
      if (!missingHere.length) setScreen('company');
      return;
    }
    setBusy(true);
    try {
      await completePartA(form);
      onCompleted?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const missing = detail?.missing || [];
      if (missing.length) {
        toast.error(`Complete ${missing.map((k) => FIELD_LABELS[k] || k).join(', ')}`);
        setScreen(missing.some((k) => k.startsWith('admin_')) && !missing.some((k) => !k.startsWith('admin_')) ? 'you' : 'company');
      } else {
        toast.error(detail?.message || detail || 'Could not complete setup');
      }
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
      data-testid="part-a-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="part-a-title"
    >
      <div className="relative flex max-h-[90vh] w-full max-w-[720px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="border-b px-6 pb-4 pt-6">
          <h2 id="part-a-title" className="text-lg font-semibold">Set up your company</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            A few details we need before you can roster, claim or notify anyone. This takes about two minutes.
          </p>
          <PartAStepper screen={screen} missingCount={form ? missingAll.length : null} className="mt-4" />
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading || !form ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
            </div>
          ) : screen === 'company' ? (
            <div className="space-y-4">
              <h3 className="text-base font-medium">Company details</h3>
              <p className="text-sm text-muted-foreground" data-testid="part-a-company-intro">{partACompanyIntro(form.phone)}</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Company name" required value={form.company_name} onChange={(v) => set('company_name', v)} disabled={locked.company_name} error={showError('company_name')} hint={locked.company_name ? 'Verified at signup. Change from Settings.' : null} />
                <Field label="ABN" required={!form.abn_exempt} value={form.abn} onChange={(v) => set('abn', v)} disabled={locked.abn} error={showError('abn')} hint={form.abn_exempt ? 'ABN exempt' : (locked.abn ? 'Verified at signup. Change from Settings.' : '11 digits')} />
                <Field label="Street address" required value={form.address} onChange={(v) => set('address', v)} className="sm:col-span-2" error={showError('address')} />
                <Field label="Suburb" required value={form.suburb} onChange={(v) => set('suburb', v)} error={showError('suburb')} />
                <div>
                  <Label className="text-xs">State <span className="text-rose-600">*</span></Label>
                  <Select value={form.state || ''} onValueChange={(v) => set('state', v)}>
                    <SelectTrigger className={`mt-1 ${showError('state') ? 'border-rose-400' : ''}`} data-testid="part-a-state"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{AU_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Field label="Postcode" required value={form.postcode} onChange={(v) => set('postcode', v)} error={showError('postcode')} />
                <Field label="Phone" value={form.phone} onChange={(v) => set('phone', v)} error={showError('phone')} hint="Optional. Australian landline, mobile, or 1300/1800" />
                <Field label="Company email" required type="email" value={form.company_email} onChange={(v) => set('company_email', v)} error={showError('company_email')} />
                <div className="sm:col-span-2">
                  <Label className="text-xs">Timezone <span className="text-rose-600">*</span></Label>
                  <Select value={form.timezone || ''} onValueChange={(v) => set('timezone', v)}>
                    <SelectTrigger className={`mt-1 ${showError('timezone') ? 'border-rose-400' : ''}`} data-testid="part-a-timezone"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{timezones.map((tz) => <SelectItem key={tz} value={tz}>{tz.replace('Australia/', '').replace('_', ' ')}</SelectItem>)}</SelectContent>
                  </Select>
                  <p className="mt-1 text-xs text-muted-foreground">Rosters, clock times and notifications use this timezone.</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <h3 className="text-base font-medium">Your details</h3>
              <p className="text-sm text-muted-foreground">This is your account as the company admin. Workers you add later have their own profiles.</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="First name" required value={form.admin_first_name} onChange={(v) => set('admin_first_name', v)} error={showError('admin_first_name')} />
                <Field label="Last name" required value={form.admin_last_name} onChange={(v) => set('admin_last_name', v)} error={showError('admin_last_name')} />
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input className="mt-1 bg-muted" value={partA?.admin_email || ''} readOnly disabled />
                </div>
                <Field label="Mobile" required value={form.admin_mobile} onChange={(v) => set('admin_mobile', v)} error={showError('admin_mobile')} hint="Used for alerts and verification" />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t px-6 py-3">
          <div className="flex items-center gap-4">
            {screen === 'you' ? (
              <button type="button" className="text-sm text-muted-foreground hover:text-foreground" onClick={() => setScreen('company')} disabled={busy}>
                Back
              </button>
            ) : null}
            <button
              type="button"
              className="text-sm text-muted-foreground hover:text-foreground"
              data-testid="part-a-sign-out"
              onClick={() => leaveToLogin(onSignOut)}
            >
              Sign out
            </button>
            <button
              type="button"
              className="text-sm text-muted-foreground hover:text-foreground"
              data-testid="part-a-finish-later"
              disabled={busy}
              onClick={() => {
                deferPartA();
                onFinishLater?.();
              }}
            >
              Finish later
            </button>
          </div>
          {screen === 'company' ? (
            <Button onClick={handleContinue} disabled={busy || !form} data-testid="part-a-continue">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save and continue'}
            </Button>
          ) : (
            <Button onClick={handleDone} disabled={busy || !form} data-testid="part-a-done">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Done'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', required, disabled, hint, error, className }) {
  return (
    <div className={className}>
      <Label className="text-xs">
        {label}{required && <span className="ml-0.5 text-rose-600">*</span>}
      </Label>
      <Input
        className={`mt-1 ${disabled ? 'bg-muted' : ''} ${error ? 'border-rose-400' : ''}`}
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-invalid={error || undefined}
      />
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export default OnboardingHubModal;
